import {
  Box,
  BoxProps,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { ComponentProps, useEffect, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { formatPercent } from "@app/utils";
import {
  ChartActiveElement,
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { ChartDataset, ChartTypeRegistry } from "chart.js";
import { CmaModelChartData } from "@app/api/carApi.generated";
import { createPendoClassName, pendoClasses } from "@app/core/thirdParty/pendo";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarIconZoomIn, CarIconZoomOut } from "@app/components/Icons";
import { CarChartLegend } from "@app/components/ChartLegend";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { CarAssetClassDftItem } from "@app/pages/assetClassAnalysis/useAssetClassSummaryStat";

type Options = ComponentProps<typeof Chart>["options"];

interface DftVersusTenYearReturnDataPoint {
  x: number;
  y: number;
}

interface DftVersusTenYearReturnData {
  xLabel: string;
  yLabel: string;
  datasets: ChartDataset<
    "scatter" | "line",
    DftVersusTenYearReturnDataPoint[]
  >[];
  range: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
  interactiveDatasetLength: number;
  dftEnd: number;
}

export const getDftVersusTenYearReturnData = ({
  theme,
  dftItem,
  forPdf,
}: {
  theme: Theme;
  dftItem: CarAssetClassDftItem;
  forPdf?: boolean;
}): DftVersusTenYearReturnData => {
  const upperBondColor = theme.palette.softBlack;
  const lowerBondColor = theme.palette.softBlack;
  const expectedReturnColor = theme.palette.chartsColor.green;
  const realReturnColor = theme.palette.chartsColor.blue;

  const getMinMax = (data: { x: number; y: number }[]) => {
    const sortedData = Array.from(data).sort((a, b) => a.x - b.x);
    return sortedData.splice(1, sortedData.length - 2);
  };
  const result: DftVersusTenYearReturnData = {
    datasets: [
      {
        type: "line",
        id: "upper_bound",
        label: "Upper Bound",
        data: getMinMax(
          dftItem.modelChartData.boundary_x?.map((i, idx) => ({
            x: i,
            y: dftItem.modelChartData.boundary_y_upper?.[idx] ?? 0,
          })) ?? [],
        ),
        borderColor: upperBondColor,
        pointBorderColor: upperBondColor,
        pointBackgroundColor: upperBondColor,
        pointBorderWidth: 1,
        pointRadius: 0,
        borderWidth: 1,
      },
      {
        type: "line",
        id: "lower_bound",
        label: "Lower Bound",
        data: getMinMax(
          dftItem.modelChartData.boundary_x?.map((i, idx) => ({
            x: i,
            y: dftItem.modelChartData.boundary_y_lower?.[idx] ?? 0,
          })) ?? [],
        ),
        borderColor: lowerBondColor,
        pointBorderColor: lowerBondColor,
        pointBackgroundColor: lowerBondColor,
        pointBorderWidth: 1,
        pointRadius: 0,
        borderWidth: 1,
      },
      {
        type: "line",
        id: "expected_return_trend",
        label: "Expected Return Trend",
        data: getMinMax(
          dftItem.modelChartData.boundary_x?.map((i, idx) => ({
            x: i,
            y: dftItem.modelChartData.boundary_y_expected_ret?.[idx] ?? 0,
          })) ?? [],
        ),
        borderColor: expectedReturnColor,
        pointBorderColor: expectedReturnColor,
        pointBackgroundColor: expectedReturnColor,
        pointBorderWidth: 1,
        pointRadius: 0,
        borderWidth: 1,
      },
      {
        type: "scatter",
        id: "ten_year_real_return",
        label: `${dftItem.name} 10 Year Real Return`,
        data:
          dftItem.modelChartData.boundary_scatter_x?.map((i, idx) => ({
            x: i,
            y: dftItem.modelChartData.boundary_scatter_y?.[idx] ?? 0,
          })) ?? [],
        borderColor: realReturnColor,
        pointBorderColor: realReturnColor,
        pointBackgroundColor: realReturnColor,
        pointBorderWidth: 1,
        pointRadius: forPdf ? 1 : 3,
        borderWidth: 1,
        hoverBorderColor: "black",
        hoverBackgroundColor: "black",
      },
    ],
    xLabel: "Real 10 Year Return vs. Distance from Trend",
    yLabel: "Compound Annual Return",
    range: {
      minX: 0,
      maxX: 0,
      minY: 0,
      maxY: 0,
    },
    interactiveDatasetLength:
      dftItem.modelChartData.boundary_scatter_x?.length ?? 0,
    dftEnd: dftItem.modelChartData.label_end_dft ?? 0,
  };

  const allData = result.datasets.flatMap((i) => i.data);

  const minX = Math.min(...allData.map((i) => i.x));
  const maxX = Math.max(...allData.map((i) => i.x));
  const offsetX = ((maxX - minX) / 100) * 2; // 2%
  const minY = Math.min(...allData.map((i) => i.y));
  const maxY = Math.max(...allData.map((i) => i.y));
  const offsetY = ((maxY - minY) / 100) * 2; // 2%

  return {
    ...result,
    range: {
      minX: minX - offsetX,
      maxX: maxX + offsetX,
      minY: minY - offsetY,
      maxY: maxY + offsetY,
    },
  };
};

interface ChartDftVersusTenYearReturnProps {
  sx?: BoxProps["sx"];
  dftItem: CarAssetClassDftItem;
  pendoPrefix: string;
  activeElement: ChartActiveElement;
  isWhiteContext: boolean;
}

export const ChartDftVersusTenYearReturn = ({
  sx,
  dftItem,
  pendoPrefix,
  activeElement,
  isWhiteContext,
}: ChartDftVersusTenYearReturnProps) => {
  const theme = useTheme();

  const { datasets, range, yLabel, xLabel, interactiveDatasetLength, dftEnd } =
    useMemo(
      () =>
        getDftVersusTenYearReturnData({
          theme,
          dftItem,
        }),
      [theme, dftItem],
    );

  const { tooltipPlugin, tooltipData } = useChartTooltip({
    chartName: "ChartDftVersusTenYearReturn",
    activeElement,
  });

  const chartRef = useRef<ChartJSOrUndefined<
    keyof ChartTypeRegistry,
    DftVersusTenYearReturnDataPoint[],
    unknown
  > | null>();

  useEffect(() => {
    if (chartRef.current) {
      if (
        activeElement.element &&
        activeElement.element.chartName === "ChartTotalReturns" &&
        activeElement.element.datasetIndex === 1 &&
        activeElement.element.index < interactiveDatasetLength
      ) {
        chartRef.current.setActiveElements([
          { datasetIndex: 3, index: activeElement.element.index },
        ]);
        chartRef.current.tooltip?.setActiveElements(
          [{ datasetIndex: 3, index: activeElement.element.index }],
          { x: 0, y: 0 },
        );
      } else if (!activeElement.element) {
        chartRef.current.setActiveElements([]);
        chartRef.current.tooltip?.setActiveElements([], { x: 0, y: 0 });
      }
    }
  }, [activeElement, interactiveDatasetLength]);

  const renderTooltip: RenderTooltip = (datasetIndex, dataIndex) => {
    const ds = datasets[datasetIndex];

    const xLabel = `Distance from Trend: ${formatPercent(
      ds?.data?.[dataIndex]?.x ?? 0,
      2,
    )}`;
    const yLabel = `Real 10 Year Return: ${formatPercent(
      ds?.data?.[dataIndex]?.y ?? 0,
      2,
    )}`;
    return (
      <>
        <Box
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: "black",
            maxWidth: 300,
            gap: 0.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="par01SemiBold" sx={{ fontSize: 15 }}>
            {ds?.label}
          </Typography>
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {xLabel}
          </Typography>
          <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
            {yLabel}
          </Typography>
        </Box>
      </>
    );
  };

  const options = useMemo(() => {
    const result: Options = {
      maintainAspectRatio: false,
      animation: {
        duration: 500,
      },
      scales: {
        x: {
          offset: false,
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: (value) =>
              typeof value === "number" ? formatPercent(value, 1) : value,
          },
          grid: {
            display: false,
          },
          type: "linear",
          min: range.minX,
          max: range.maxX,
        },
        y: {
          offset: false,
          type: "linear",
          title: {
            text: yLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },

          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: (value) =>
              typeof value === "number" ? formatPercent(value, 1) : value,
          },
          grid: ChartUtils.getGridOptions({ theme }),
          min: range.minY,
          max: range.maxY,
        },
      },
      interaction: {
        mode: "point",
        intersect: false,
      },
      plugins: {
        roundedBackground: ChartUtils.getRoundedBackgroundOptions({
          theme,
          isWhiteContext,
        }),
        rangeHighlight: {
          labelPosition: "horizontal",
          borderColor: theme.palette.gray4,
          font: {
            family: theme.typography.fontFamily,
            size: 14,
            weight: 600,
          },
          textColor: theme.palette.softBlack,
          items: [
            {
              value: dftEnd,
              maxDelta: 5,
              minDelta: -5,
              minLabel: "DFT",
              maxLabel: "DFT",
              backgroundColor: "#fdf5ee",
            },
            // {
            //   value: "tooltip",
            //   minDelta: -5,
            //   maxDelta: 5,
            //   minLabel: "-5",
            //   maxLabel: "+5",
            //   backgroundColor: "#f6f7f8",
            // },
          ],
        },
        tooltip: tooltipPlugin,
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
          },
          limits: {
            x: {
              min: "original",
              max: "original",
              // minRange: 5 * 365 * 24 * 60 * 60 * 1000, // max zoom level - 5 years
            },
          },
          zoom: {
            pinch: {
              enabled: true,
            },
            mode: "x",
          },
        },
      },
    };
    return result;
  }, [range, theme, tooltipPlugin, yLabel, isWhiteContext, dftEnd]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: 440,
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", zIndex: 1, alignItems: "flex-end" }}>
        <Typography
          variant="par03SemiBold"
          sx={{ fontSize: "19px", mb: 0.5, ml: 10 }}
        >
          {xLabel}
          <CarTooltipBox
            sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
            className={pendoClasses.assetClassAnalysisDftRealTenYearReturn}
          />
        </Typography>

        <IconButton
          sx={{ ml: "auto" }}
          size="small"
          onClick={() => {
            chartRef.current?.zoom(1.5, "zoom");
          }}
        >
          <CarIconZoomIn fontSize="large" color="primary" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => {
            chartRef.current?.zoom(-1.5, "zoom");
          }}
        >
          <CarIconZoomOut fontSize="large" color="primary" />
        </IconButton>
      </Box>
      <ChartTooltip
        tooltipData={tooltipData}
        renderTooltip={renderTooltip}
        shiftY={0}
        sx={{
          /* Some bug: can't specify height on chart because it starts to grow, so put chart in the box */
          height: 440,
          mt: -1,
          position: "relative",
        }}
      >
        <CarTooltipBox
          sx={{ position: "absolute", left: 5, top: 48 }}
          className={pendoClasses.assetClassAnalysisDftCompoundAnnualReturn}
        />
        <Chart
          className={createPendoClassName(`${pendoPrefix}_chart`)}
          ref={chartRef}
          onDoubleClick={() => {
            chartRef.current?.resetZoom();
          }}
          type="line"
          datasetIdKey="id"
          data={{ datasets }}
          options={options}
          plugins={[
            ChartPlugins.roundedBackground,
            ChartPlugins.rangeHighlight,
          ]}
        />
      </ChartTooltip>

      <CarChartLegend
        sx={{
          mt: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
        labelVariant="caption"
        items={[
          {
            label: (
              <>
                Expected return trend line
                <CarTooltipBox
                  sx={{ display: "inline-block", ml: 0.5, mb: -0.6 }}
                  className={
                    pendoClasses.assetClassAnalysisDftExpectedReturnTrendLine
                  }
                />
              </>
            ),
            color: theme.palette.chartsColor.green,
            dataIndex: 0,
            datasetIndex: 0,
          },
          {
            label: (
              <>
                Upper & Lower Bounds
                <CarTooltipBox
                  sx={{ display: "inline-block", ml: 0.5, mb: -0.6 }}
                  className={
                    pendoClasses.assetClassAnalysisDftUpperAndLowerBounds
                  }
                />
              </>
            ),
            color: theme.palette.softBlack,
            dataIndex: 0,
            datasetIndex: 1,
          },
          {
            label: (
              <>
                Asset Class 10-Year Real Return
                <CarTooltipBox
                  sx={{ display: "inline-block", ml: 0.5, mb: -0.6 }}
                  className={
                    pendoClasses.assetClassAnalysisDftAssetClassTenYearRealReturn
                  }
                />
              </>
            ),
            color: theme.palette.chartsColor.blue,
            isCircle: true,
            dataIndex: 0,
            datasetIndex: 2,
          },
        ]}
      />
    </Box>
  );
};
